import { addBreadcrumb, captureMessage } from "@sentry/react";
import { getStoredValue } from "../services/logics";

// Once per page load each: these fire on every expiry, and one event per visit is what we need to
// see who hit it and why - not one per click. User/store context comes from App's Sentry scope.
let screenReported = false;
let blockedRequestReported = false;
let blockedRequestCount = 0;

const deadlineContext = () => {
  const deadline = getStoredValue("session_expires_at") || null;
  const parsed = Date.parse(deadline || "");
  return {
    session_expires_at: deadline,
    minutes_past_deadline: Number.isNaN(parsed) ? null : Math.round((Date.now() - parsed) / 60000),
    token_issued_at: getStoredValue("token_issued_at") || null,
  };
};

/** The session-expired screen replaced the app. reason: "deadline" (clock) or "auth_failure". */
export const reportSessionExpiredShown = reason => {
  if (screenReported) return;
  screenReported = true;
  captureMessage("Session expired screen shown", {
    level: "info",
    fingerprint: ["session-expired-screen", reason],
    tags: { feature: "session", session_expiry_reason: reason, route_path: window.location.pathname },
    extra: deadlineContext(),
  });
};

/**
 * An authenticated request was attempted after the session was marked expired (and was not sent).
 * With the app unmounted behind the expired screen this should not happen; if it does, something is
 * still driving the page - e.g. a browser extension that removed the screen, as in the Sep 2026
 * "clicks do nothing" incident, which went unseen because nothing reported it.
 */
export const reportRequestWhileExpired = config => {
  blockedRequestCount += 1;
  const request = `${(config?.method || "get").toUpperCase()} ${config?.url || ""}`;
  addBreadcrumb({
    category: "session",
    level: "warning",
    message: `Blocked request while session expired: ${request}`,
  });
  if (blockedRequestReported) return;
  blockedRequestReported = true;
  captureMessage("Request attempted while session expired", {
    level: "warning",
    fingerprint: ["request-while-session-expired"],
    tags: { feature: "session", route_path: window.location.pathname },
    extra: { first_request: request, ...deadlineContext() },
  });
};

export const blockedRequestsWhileExpired = () => blockedRequestCount;

// Test hook: the flags are per page load, which a test file spans many of.
export const resetSessionTelemetry = () => {
  screenReported = false;
  blockedRequestReported = false;
  blockedRequestCount = 0;
};
